import React, { useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import {
  MessageCircle,
  Inbox,
  Minimize,
  Maximize,
  SendHorizonal,
} from "lucide-react";

const ChatWidget = ({
  tramiteRequestId,
  isVisible,
  onToggleVisibility,
  tramiteName,
}) => {
  const [messages, setMessages] = useState([
    {
      id: "1",
      sender_name: "Sistema MisTramites",
      sender_role: "system",
      message_text: `¡Bienvenido al chat de tu trámite: ${tramiteName || "Trámite"}! Tu gestor asignado te contactará pronto para ayudarte con cualquier duda.`,
      is_system_message: true,
      created_at: new Date().toISOString(),
    },
  ]);
  const [messageInput, setMessageInput] = useState("");
  const [roomName, setRoomName] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const [myRole, setMyRole] = useState("");
  const [name, setName] = useState("");
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const url = process.env.REACT_APP_BACKEND_URL;

    const s = io(url, {
      auth: { token },
      transports: ["polling"],
    });

    socketRef.current = s;

    s.on("disconnect", (reason) => {
      console.log("Disconnect:", reason);
    });

    s.on("connect_error", (err) => {
      console.log("❌ connect_error:", err.message);
    });

    // eventos del chat
    s.on("room_joined", (data) => {
      setRoomName(data.room_name);
      setMyRole(data.user_role);
      setName(data.user_name);
      setMessages(data.message_history || []);
    });

    s.on("new_message", (msg) => {
      setMessages((prev) => [...prev, msg]);
    });

    return () => {
      s.disconnect();
    };
  }, []); //

  useEffect(() => {
    if (!socketRef.current) return;
    if (!isVisible) return;

    console.log("📡 join_room:", tramiteRequestId);

    socketRef.current.emit("join_room", {
      application: tramiteRequestId,
    });
  }, [isVisible, tramiteRequestId]);

  // Auto-scroll a nuevos mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (!messageInput.trim() || !socketRef.current || !roomName) return;

    // Simular envío de mensaje
    const newMessage = {
      id: Date.now().toString(),
      message_text: messageInput,
      is_system_message: false,
      created_at: new Date().toISOString(),
    };

    // setMessages(prev => [...prev, newMessage]);

    // Simular respuesta automática del gestor después de 2 segundos
    /* setTimeout(() => {
      const response = {
        id: (Date.now() + 1).toString(),
        sender_name: 'Gestor Especializado',
        sender_role: 'gestor',
        message_text: 'Gracias por tu mensaje. He recibido tu consulta y te responderé a la brevedad.',
        is_system_message: false,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, response]);
    }, 2000); */

    socketRef.current.emit("send_message", {
      room_name: roomName,
      application: tramiteRequestId,
      message: messageInput,
    });

    setMessageInput("");
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
    return date.toLocaleDateString();
  };

  return (
    <div
      className={`w-full bg-white rounded-lg border border-gray-200 transition-all duration-300 ${isVisible ? "block" : "hidden"} ${isMinimized ? "h-16" : "h-96"}`}
    >
      {/* Header del chat */}
      <div className="flex items-center justify-between p-4 bg-blue-600 text-white rounded-t-lg">
        <div className="flex items-center space-x-3">
          <span className="text-xl">
            <MessageCircle />
          </span>
          <div>
            <h3 className="font-semibold text-sm">
              Chat - {tramiteName || "Trámite"}
            </h3>
            <div className="flex items-center space-x-2 text-xs">
              <span className="text-green-300">🟢</span>
              <span>En línea (Demo)</span>
              <span>• Chat en tiempo real próximamente</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-white hover:bg-blue-700 p-1 rounded transition-colors"
            title={isMinimized ? "Expandir" : "Minimizar"}
          >
            {isMinimized ? <Maximize /> : <Minimize />}
          </button>
          <button
            onClick={onToggleVisibility}
            className="text-white hover:bg-blue-700 p-1 rounded transition-colors"
            title="Cerrar chat"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Contenido del chat */}
      {!isMinimized && (
        <>
          {/* Área de mensajes */}
          <div className="flex-1 overflow-y-auto p-4 h-64 bg-gray-50">
            <div className="space-y-3">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.sender_role === myRole}
                  timestamp={formatMessageTime(message.created_at)}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input de mensaje */}
          <div className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <input
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                maxLength={500}
              />
              <button
                type="submit"
                disabled={!messageInput.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SendHorizonal />
              </button>
            </form>

            {/* Información del chat */}
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>{messages.length} mensajes</span>
              <span>Sala: application_{tramiteRequestId}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const MessageBubble = ({ message, isOwn, timestamp }) => {
  const isSystemMessage = message.is_system_message;

  if (isSystemMessage) {
    return (
      <div className="text-center">
        <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-xs">
          🔔 {message.message_text}
        </div>
        <div className="text-xs text-gray-400 mt-1">{timestamp}</div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
          isOwn
            ? "bg-blue-600 text-white rounded-br-sm"
            : "bg-white text-gray-900 rounded-bl-sm border border-gray-200"
        }`}
      >
        {!isOwn && (
          <div className="text-xs font-medium mb-1 text-gray-500">
            {message.sender_role === "gestor" ? "👨‍💼 " : "👤 "}
            {message.sender_name}
          </div>
        )}
        <div className="text-sm leading-relaxed">{message.message_text}</div>
        <div
          className={`text-xs mt-1 ${isOwn ? "text-blue-100" : "text-gray-400"}`}
        >
          {timestamp}
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;
